/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Badge, message, Tooltip } from "antd";
import { useDebounce } from "../../../utils/hooks";
import { AUTH_TOKEN, CustomTagRender, DOMAIN_ID, ORG_ID, isEmpty } from "../../../utils/helpers";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import DataGridTable from "../../../components/ui/datatable/DataGridTable";
import { FilterComponents } from "../../../components/ui/common";
import { AppContentLayout, PageSection } from "../../../layout/applayout";
import { getInstanceList } from "../../../service/pamService";
import { getOSList } from "../../../service/tenantService";

const Endpoints = () => {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [endpointData, setEndpointData] = useState([]);
  const [originalEpm, setOriginalEpm] = useState([]);
  const [osList, setOsList] = useState([]);

  //pagination
  const PAGESIZE = 10;
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationParams, setPaginationParams] = useState({
    page: 0,
    pageSize: PAGESIZE,
  });

  // UI configuration
  const filterByOptions = useMemo(() => {
    const option = [
      {
        value: "State",
        label: "State",
        children: [
          {
            value: "active",
            label: "Active",
          },
          {
            value: "inactive",
            label: "Inactive",
          },
        ],
      },
      {
        value: "osname",
        label: "OS",
        children: osList,
      },
    ];
    return option;
  }, [osList]);

  /// DataTable
  const tableStructure = useMemo(() => {
    const baseColumns = [
      {
        key: "hostName",
        field: "hostName",
        flex: 1.5,
        headerName: "Host Name",
        renderCell: (val) => (
          <CustomTagRender value={val.row.hostName} />
        ),
      },
      { key: "description", field: "description", headerName: "Description" },
      { key: "osname", field: "osname", headerName: "OS" },
      {
        headerName: "Public IP",
        field: "publicIp",
        key: "publicIp",
      },
      { headerName: "Private IP", field: "privateIp", key: "privateIp" },
      {
        headerName: "FQDN",
        field: "fqdn",
        key: "fqdn",
      },
      {
        headerName: "Status",
        field: "status",
        key: "status",
      },
      {
        headerName: "Agent Status",
        field: "AgentStatus",
        key: "AgentStatus",
        renderCell: (val) => EndpointStatus(val.row.lastActive),
      },
    ];

    return baseColumns;
  }, []);

  /// Filter
  const [search, setSearch] = useState("");
  const debouncedValue = useDebounce(search, 600);
  const [filterParams, setFilterParams] = useState({
    search: "",
    filter: {
      filterBy: "",
      value: "",
    },
  });

  const [filterstate, setFilterState] = useState([
    // { isFilter: true, labelName: "Filter by", inputName: "filter", placeholder: "Select to search", options: [], handleOnChange: null, handleOnClear: null },
    {
      isAutocomplete: true,
      inputValue: "",
      placeholder: "Search by host name",
      inputName: "search",
      labelName: "Search",
      handleSearch: (value) => setSearch(value),
      results: [],
      handleOnChange: null,
      handleOnClear: null,
    },
  ]);

  const handleChangeAutocomplete = (value, name) => {
    setSearch(value);
    setFilterParams((prev) => ({ ...prev, [name]: value }));
    setFilterState((prevState) => {
      return prevState?.map((item) => {
        if (item?.isAutocomplete && item?.inputName === name) return { ...item, inputValue: value };
        return item;
      });
    });
  };

  const handleOnClear = (name) => {
    const value = name === 'filter' ? { filterBy: "", value: "" } : ""
    setFilterParams((prev) => ({ ...prev, [name]: value }));
  }

  const handleFilterBy = (data, name) => {
    setFilterParams((prev) => ({ ...prev, [name]: { filterBy: data ? data[0]?.value : "", value: data ? data[1]?.value : "" } }));
  };

  useEffect(() => {
    const filteredOptions = isEmpty(search)
      ? []
      : endpointData
          .filter((endpointdata) => endpointdata.hostName.toLowerCase().includes(search.toLowerCase()))
          .map((endpointdata) => ({
            label: endpointdata.hostName,
            value: endpointdata.hostName,
          }));

    setFilterState((prevState) => {
      return prevState?.map((item) => {
        if (item?.isAutocomplete) return { ...item, results: filteredOptions };
        return item;
      });
    });
  }, [search]);

  // lifeCycle hooks
  useEffect(() => {
    setFilterState((prevState) => {
      return prevState?.map((item) => {
        if (item?.isAutocomplete) return { ...item, handleOnChange: handleChangeAutocomplete, handleOnClear }
        if (item?.isFilter) return { ...item, handleOnChange: handleFilterBy, handleOnClear }
        return item;
      })
    })
  }, []);

  useEffect(() => {
    setFilterState((prevState) => {
      return prevState?.map((item) => {
        if (item?.isFilter) return { ...item, options: filterByOptions };
        return item;
      });
    });

    if (osList.length) {
      const dataWithOs = endpointData.map((epm) => {
        const osItem = osList.find((os) => os.value === epm.osName);
        return {
          ...epm,
          osname: osItem ? osItem.label : epm.osName,
        };
      });
      setEndpointData(dataWithOs);
      setIsLoading(false);
    }
  }, [osList]);

  useEffect(() => {
    fetchInstance();
  }, [debouncedValue, filterParams, paginationParams]);

  // API calls
  const fetchInstance = () => {
    let payload = {
      domainId: DOMAIN_ID(),
      pageId: paginationParams.page + 1,
      pageSize: paginationParams.pageSize,
      search: filterParams.search,
      filter: filterParams.filter,
      token: AUTH_TOKEN(),
      orgId: ORG_ID(),
      MachineType: "endpoint",
    };
    setIsLoading(true);
    getInstanceList({ payload, onSuccess: handleInstanceSuccess, onFailure: handleInstanceFailure });
  };

  const handleInstanceSuccess = (data) => {
    const originalEpm = data?.instances;
    setOriginalEpm(originalEpm);
    const epmWithId = originalEpm.map((epm, index) => ({
      ...epm,
      id: index + 1,
      countOfUsers: 0,
      countOfGroups: 0,
      osname: epm.osName,
    }));
    setEndpointData(epmWithId ?? []);
    setTotalCount(data?.totalCount);
    setTimeout(() => fetchInstanceOsType(), 800);
  };

  const handleInstanceFailure = useCallback(() => {
    messageApi.open({
      type: "error",
      content: "Unable to fetch endpoints",
    });
    setIsLoading(false);
  }, []);

  const fetchInstanceOsType = () => {
    let payload = {
      tenantId: DOMAIN_ID(),
      orgId: ORG_ID(),
    };
    getOSList({ payload, onSuccess: handleOSListSuccess, onFailure: handleOSListFailure });
  };

  const handleOSListSuccess = (data) => {
    const osData = data?.osList.map((os) => ({
      value: os.id.toString(),
      label: os.osName.toLowerCase() === "windows" ? "Windows" : os.osName.toLowerCase() === "linux" ? "Linux" : os.osName,
    }));
    setOsList(osData ?? []);
  };

  const handleOSListFailure = useCallback(() => {
    setIsLoading(false);
    messageApi.open({
      type: "error",
      content: "Unable to fetch OS",
    });
  }, []);

  // Methods and handlers

  const EndpointStatus = (lastUpdated) => {
    const timezone = localStorage.getItem("timezone");
    const lastActive = moment.unix(lastUpdated).tz(timezone).format("YYYY-MM-DD HH:mm:ss");
    const diff = moment().diff(lastActive, "minutes");
    let hours = Math.floor(diff / 60);
    let days = Math.floor(hours / 24);
    let minutes = diff % 60;

    let message;

    if (days > 0) {
      message = `Last Active: ${days} days ago`;
    } else if (hours > 0) {
      message = `Last Active: ${hours} hours${minutes > 0 ? ` and ${minutes} minutes ago` : " hours ago"}`;
    } else {
      message = `Last Active: ${diff} minutes ago`;
    }

    if (diff > 20) {
      return (
        <Tooltip title={message}>
          <Badge status="error" text="Offline" />
        </Tooltip>
      );
    } else if (diff > 10) {
      return (
        <Tooltip title={message}>
          <Badge status="warning" text="Offline" />
        </Tooltip>
      );
    }
    return <Badge status="success" text="Online" />;
  };

  return (
    <>
      {contextHolder}
      <AppContentLayout
        title={<PageSection screenName="Endpoints" screenDescription="Endpoints list of your organization" />}
        cardChildren={
          <>
            <div className="px-6 py-4">
              <FilterComponents uiStructure={filterstate} />
              <DataGridTable
                tableStructure={tableStructure}
                data={endpointData}
                loading={isLoading}
                totalCount={totalCount}
                pagination={paginationParams}
                handleChangePagination={
                  ({ page, pageSize }) => { setPaginationParams({ page, pageSize }); }
                }
              />
            </div>
          </>
        }
      />
    </>
  );
};

export default Endpoints;
